import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PRODUCTS } from "../../data/site";

export const Products = ({ limit, singlePage, products: productsProp }) => {
  const source = productsProp || PRODUCTS;
  const list = limit ? source.slice(0, limit) : source;

  return (
    <section data-testid="products-section" className="relative py-24 md:py-32 bg-white">
      <div className="relative max-w-7xl mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <div className="text-[11px] uppercase tracking-[0.22em] text-amber-600 font-bold flex items-center gap-2">
              <span className="w-8 h-px bg-amber-500" />
              Products
            </div>
            <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-bold text-navy-900 tracking-[-0.03em] leading-[1]">
              Production-ready hardware,
              <br />
              <span className="text-amber-500">built to outlast.</span>
            </h2>
          </motion.div>
          <Link
            to="/products"
            data-testid="products-view-all"
            className={`group inline-flex items-center gap-2 text-sm text-navy-900 font-semibold tracking-tight hover:gap-3 transition-all ${singlePage ? "hidden" : ""}`}
          >
            View entire catalog
            <ArrowUpRight className="w-4 h-4 text-amber-600 group-hover:rotate-45 transition-transform" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {list.map((p, i) => (
            <motion.article
              key={p.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.08 }}
              className="lift-card group relative bg-white border border-slate-200 rounded-2xl overflow-hidden"
              data-testid={`product-card-${p.slug}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                <img
                  src={p.image}
                  alt={p.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1000ms] ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/40 via-transparent to-transparent" />
                <div className="absolute top-4 left-4 w-10 h-10 rounded-xl bg-white/95 backdrop-blur flex items-center justify-center text-navy-900 shadow">
                  <p.icon className="w-5 h-5" />
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-display text-lg font-bold text-navy-900 tracking-tight">
                  {p.title}
                </h3>
                <p className="mt-2 text-slate-600 text-sm leading-relaxed line-clamp-2">
                  {p.short}
                </p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {p.labels.map((t) => (
                    <span
                      key={t}
                      className="text-[10px] uppercase tracking-[0.14em] font-bold text-navy-800 bg-amber-50 border border-amber-200 px-2 py-1 rounded-md"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Products;
